import { Divider, Popover } from '@ltvco/refresh-lib/theme';
import { Close } from '@mui/icons-material';
import React from 'react';

import {
  PopoverContainer,
  Header,
  Title,
  CloseButton,
  Description,
  OperatorRow,
  OperatorSymbolBox,
  OperatorSymbol,
  OperatorInfo,
  OperatorName,
  OperatorExample,
  HighlightedText,
  OperatorsColumn,
  OperatorsColumnsContainer,
} from './components';

// ============= TYPES =============
export interface SearchOperator {
  id: string;
  symbol: string;
  name: string;
  example: string;
  exampleParts: string[];
}

export interface SearchOperatorsData {
  title: string;
  description: string;
  operators: SearchOperator[];
}

export interface SearchOperatorsPopoverProps {
  /** Anchor element for the popover */
  anchorEl: HTMLElement | null;
  /** Whether the popover is open */
  open: boolean;
  /** Callback when the popover should close */
  onClose: () => void;
  /** Operators data to display */
  operatorsData: SearchOperatorsData;
  /** Test ID for the component */
  testId?: string;
}

// ============= HELPER FUNCTION =============
const renderExample = (exampleParts: string[]) => {
  // Operators to highlight
  const operators = [
    'AND',
    'OR',
    '-',
    '"',
    ':',
    '*',
    '?',
    'TO',
    '()',
    '[x TO y]',
  ];

  return exampleParts.map((part, index) => {
    const isOperator = operators.includes(part);
    if (isOperator) {
      return <HighlightedText key={index}>{part}</HighlightedText>;
    }
    return <span key={index}>{part}</span>;
  });
};

// ============= COMPONENT =============
export const SearchOperatorsPopover: React.FC<SearchOperatorsPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  operatorsData,
  testId = 'search-operators-popover',
}) => {
  // Split operators into two columns
  const halfLength = Math.ceil(operatorsData.operators.length / 2);
  const leftColumnOperators = operatorsData.operators.slice(0, halfLength);
  const rightColumnOperators = operatorsData.operators.slice(halfLength);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            mt: 1,
          },
        },
      }}
      data-testid={testId}
    >
      <PopoverContainer>
        {/* Header */}
        <Header>
          <Title>{operatorsData.title}</Title>
          <CloseButton onClick={onClose} aria-label="Close">
            <Close />
          </CloseButton>
        </Header>

        {/* Description */}
        <Description>{operatorsData.description}</Description>

        {/* Divider */}
        <Divider sx={{ mb: 2, borderColor: 'divider' }} />

        {/* Operators Grid - Two columns on desktop, single column on mobile */}
        <OperatorsColumnsContainer>
          {/* Left Column */}
          <OperatorsColumn>
            {leftColumnOperators.map((operator) => (
              <OperatorRow key={operator.id}>
                <OperatorSymbolBox>
                  <OperatorSymbol>{operator.symbol}</OperatorSymbol>
                </OperatorSymbolBox>
                <OperatorInfo>
                  <OperatorName>{operator.name}</OperatorName>
                  <OperatorExample>
                    {renderExample(operator.exampleParts)}
                  </OperatorExample>
                </OperatorInfo>
              </OperatorRow>
            ))}
          </OperatorsColumn>

          {/* Right Column */}
          <OperatorsColumn>
            {rightColumnOperators.map((operator) => (
              <OperatorRow key={operator.id}>
                <OperatorSymbolBox>
                  <OperatorSymbol>{operator.symbol}</OperatorSymbol>
                </OperatorSymbolBox>
                <OperatorInfo>
                  <OperatorName>{operator.name}</OperatorName>
                  <OperatorExample>
                    {renderExample(operator.exampleParts)}
                  </OperatorExample>
                </OperatorInfo>
              </OperatorRow>
            ))}
          </OperatorsColumn>
        </OperatorsColumnsContainer>
      </PopoverContainer>
    </Popover>
  );
};
